import { getStringCookie, getJacketSkuFromSkuArray, formatAsSegmentDate, getCookie, getDevice } from './utils';
import { Filter, Item, Bundle, Visualizer, Customer, GtUser } from '../types';

type HtoFloatingButtonClickedParams = {
  itemName: string;
  itemId: number;
};

export type StaticSiteBlockViewedProperties = {
  block_type: string;
  block_key: string;
  block_name: string;
  block_index: number;
  page_slug: string;
  page_id: string;
  page_name: string;
};

export type StaticSiteClickProperties = {
  block_type: string;
  block_key: string;
  block_name: string;
  block_index: number;
  page_slug: string;
  page_id: string;
  page_name: string;
  element_type: string;
  element_name: string;
  element_href: string;
};

export type StaticSiteNavOpenedProperties = {
  nav_region: string;
  nav_surface: string;
  item_key: string;
  item_name: string;
  nav_depth: number;
  page_slug: string;
  page_id?: string;
  page_name?: string;
};

export type StaticSiteNavClickProperties = StaticSiteNavOpenedProperties & {
  item_href: string;
  nav_path: string;
  parent_key?: string;
  item_index: number;
  element_type: string;
  is_external: boolean;
  footer_group?: string;
};

export const getConsentPreferences = () => ({
  consent: {
    categoryPreferences: {
      necessary: true,
      preferences: window.Cookiebot.consent.preferences,
      statistics: window.Cookiebot.consent.statistics,
      marketing: window.Cookiebot.consent.marketing,
    },
  },
});

const segmentIsLoaded = () => window.analytics !== undefined;

const trackFn = (action: string, data: any = null, i = 0) => {
  const context = getConsentPreferences();

  if (segmentIsLoaded()) {
    if (data === null) {
      window.analytics.track(action, undefined, { context });
      return true;
    }
    window.analytics.track(action, data, { context });
    return true;
  }
  if (i < 5) {
    const n = i + 1;
    setTimeout(() => trackFn(action, data, n), 500);
  }
  return false;
};

const track = {
  product: {
    viewed: (data: unknown) => trackFn('Product Viewed', data),
    htoFloatingButton: (data: HtoFloatingButtonClickedParams) => trackFn('HTO Floating Button', data),
  },
  user: {
    loggedIn: (data: unknown) => trackFn('User Logged In', data),
    cxLoggedIn: (data: unknown) => trackFn('CX Logged In As Customer', data),
    redirectedFromAlternateDomain: (data: { domainName: string }) => trackFn('User Redirected From Alternate Domain', data),
    openedCovidBar: () => trackFn('COVID Bar Click'),
  },
  productList: {
    viewed: (data: unknown) => trackFn('Product List Viewed', data),
    filtered: (data: unknown) => trackFn('Product List Filtered', data),
  },
  vto: {
    healthCheckFailed: (data: { status: number }) => trackFn('VTO Health Check Failed', data),
    startButtonClicked: () => trackFn('VTO Button Clicked'),
    registered: (data: { email: string }) => trackFn('VTO Registered', data),
  },
  error: {
    pageNotFound: () => trackFn('404 Error'),
  },
  staticSite: {
    blockViewed: (data: StaticSiteBlockViewedProperties) => trackFn('Static Site Block Viewed', data),
    click: (data: StaticSiteClickProperties) => trackFn('Static Site Click', data),
    navClick: (data: StaticSiteNavClickProperties) => trackFn('Static Site Nav Click', data),
    navOpened: (data: StaticSiteNavOpenedProperties) => trackFn('Static Site Nav Opened', data),
  },
};

export const getSkus = (product: Item) => {
  if (product.type === 'tuxedo' || product.type === 'suit') {
    // If product is a tuxedo or a suit, we need the jacket sku.
    const jacketSku = getJacketSkuFromSkuArray((product as Bundle).products!.map((p) => p.sku));
    return [jacketSku];
  } else if (product.type === 'visualizer') {
    return (product as Visualizer).skus;
  }
  return [product.sku];
};

export const getProductImageUrl = (product: Item) => {
  const media = product.media || [];

  const image = media.filter((image) => {
    const label = image.label || '';

    return label.toLowerCase().includes('pdp_hero') || label.toLowerCase().includes('layflat');
  });

  image.push({
    url: 'https://gentux.imgix.net/no-image.png',
  });

  return image[0].url;
};

export const productListViewed = (products: Item[], productCategory: string) => {
  try {
    const productsViewed = products.map((product) => {
      const skus = getSkus(product);

      return {
        product_id: product.id,
        name: product.displayName,
        sku: skus.join(),
        category: product.category,
        price: parseFloat(product.cost!).toFixed(2),
        currency: 'USD',
        brand: 'Gentux',
        image_url: getProductImageUrl(product),
      };
    });

    track.productList.viewed({
      list_id: productCategory,
      products: productsViewed,
      category: productCategory,
    });
  } catch (e) {
    console.error(e);
  }
};

export const productListFiltered = (category: string, filters: Filter, products: Item[]) => {
  try {
    const filteredProducts = products.map((product) => ({
      product_id: product.id,
      name: product.displayName,
      sku: getSkus(product).join(),
      category: product.category,
      price: parseFloat(product.cost!).toFixed(2),
      currency: 'USD',
      brand: 'Gentux',
      image_url: getProductImageUrl(product),
    }));

    let filterList = [];

    filters.colors.length > 0 &&
      filterList.push({
        type: 'color',
        value: filters.colors.join(),
      });

    filters.patterns.length > 0 &&
      filterList.push({
        type: 'pattern',
        value: filters.patterns.join(),
      });

    filters.categories.length > 0 &&
      filterList.push({
        type: 'category',
        value: filters.categories.map((filter) => filter.displayName).join(),
      });

    filters.procurement.length > 0 &&
      filterList.push({
        type: 'procurement',
        value: filters.procurement.join(),
      });

    filters.tieTypes.length > 0 &&
      filterList.push({
        type: 'tieTypes',
        value: filters.tieTypes.join(),
      });

    filters.davidsBridal.length > 0 &&
      filterList.push({
        type: 'davidsBridal',
        value: filters.davidsBridal.join(),
      });

    track.productList.filtered({
      filters: filterList,
      category: category,
      products: filteredProducts,
    });
  } catch (e) {
    console.error(e);
  }
};

export const loggedIn = (customer: GtUser | Customer) => {
  try {
    track.user.loggedIn({
      campaignId: getStringCookie('iterableEmailCampaignId') !== undefined ? Number(getStringCookie('iterableEmailCampaignId')) : null,
      date: formatAsSegmentDate(new Date()),
      primaryEventId: customer.primaryEventId,
      customerId: customer.id,
      email: customer.email,
      eventMemberType: null,
      initial_click: getCookie('initial_click_v1'),
      first_click: getCookie('first_click_v1'),
      last_click: getCookie('last_click_v1'),
      deviceUserAgent: getDevice(window.navigator),
      partyRole: null,
    });
  } catch (e) {
    console.error(e);
  }
};

export const productsViewed = (skus: string[], data: unknown = null) => {
  try {
    if (data !== null) {
      track.product.viewed(data);
    } else {
      track.product.viewed({
        sku: skus,
      });
    }
  } catch (e) {
    console.error(e);
  }
};

export const trackCxLogin = (data: unknown) => {
  try {
    track.user.cxLoggedIn({ data });
  } catch (e) {
    console.error(e);
  }
};

export const htoFloatingButtonClicked = (data: HtoFloatingButtonClickedParams) => {
  try {
    track.product.htoFloatingButton(data);
  } catch (e) {
    console.error(e);
  }
};

/**
 * Represents cases in which a user was redirected to the site from one of our other
 * domains, e.g. `generationtux.ca`
 */
export const userRedirectedFromAlternateDomain = (domainName: string) => {
  try {
    track.user.redirectedFromAlternateDomain({ domainName });
  } catch (e) {
    console.error(e);
  }
};

/**
 * Represents when a user opens the COVID bar in the header
 */
export const covidBarOpened = () => {
  try {
    track.user.openedCovidBar();
  } catch (e) {
    console.error(e);
  }
};

/**
 * Represents a problem with Lola Dre's health check endpoint
 *
 * @param status Either the HTTP status code returned with the response, or `0` if the connection failed to open
 */
export const trackHealthCheckFailed = (status: number) => {
  try {
    track.vto.healthCheckFailed({ status });
  } catch (e) {
    console.error(e);
  }
};

/**
 * Represents the action taken by a user when opening the virtual try-on modal
 */
export const trackVtoButtonClicked = () => {
  try {
    track.vto.startButtonClicked();
  } catch (e) {
    console.error(e);
  }
};

/**
 * Represents a user having "unlocked" virtual try-on, by providing their email address
 */
export const trackVtoRegistered = (email: string) => {
  try {
    track.vto.registered({ email });
  } catch (e) {
    console.error(e);
  }
};

export const staticSiteBlockViewed = (data: StaticSiteBlockViewedProperties) => {
  try {
    track.staticSite.blockViewed(data);
  } catch (e) {
    console.error(e);
  }
};

export const staticSiteClick = (data: StaticSiteClickProperties) => {
  try {
    track.staticSite.click(data);
  } catch (e) {
    console.error(e);
  }
};

export const staticSiteNavClick = (data: StaticSiteNavClickProperties) => {
  try {
    track.staticSite.navClick(data);
  } catch (e) {
    console.error(e);
  }
};

export const staticSiteNavOpened = (data: StaticSiteNavOpenedProperties) => {
  try {
    track.staticSite.navOpened(data);
  } catch (e) {
    console.error(e);
  }
};

export const trackPageNotFoundError = () => {
  try {
    track.error.pageNotFound();
  } catch (e) {
    console.error(e);
  }
};
